import React, { useState, useRef } from 'react';
import { useImportDivisionConfiguration } from '../../hooks/use-divisions';
import { useModalA11y } from '../../hooks/use-modal-a11y';
import { DivisionConfigExport, DivisionConfigImportRequest, DivisionConfigImportResult } from '../../types';
import { Button } from '../ui/button';
import { Upload, CheckCircle, AlertCircle, FileJson } from 'lucide-react';

interface DivisionConfigImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  divisionId: string;
  divisionName: string;
}

type Step = 'upload' | 'preview' | 'result';

export function DivisionConfigImportModal({
  isOpen,
  onClose,
  divisionId,
  divisionName,
}: DivisionConfigImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [config, setConfig] = useState<DivisionConfigExport | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [importSettings, setImportSettings] = useState(true);
  const [importRoles, setImportRoles] = useState(true);
  const [importResult, setImportResult] = useState<DivisionConfigImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const importMutation = useImportDivisionConfiguration();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setParseError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        if (!parsed.schemaVersion || !Array.isArray(parsed.matchIdentifiers) || !Array.isArray(parsed.roles)) {
          setParseError('This file does not appear to be a valid division configuration export.');
          return;
        }
        setConfig(parsed as DivisionConfigExport);
        setStep('preview');
      } catch {
        setParseError('Failed to parse JSON file. Please make sure it is a valid division configuration export.');
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!config) return;

    const request: DivisionConfigImportRequest = {
      configuration: config,
      importSettings,
      importRoles,
    };

    try {
      const result = await importMutation.mutateAsync({ divisionId, request });
      setImportResult(result);
      setStep('result');
    } catch (err: any) {
      setParseError(err?.response?.data?.message || 'An error occurred during import.');
    }
  };

  const handleClose = () => {
    setStep('upload');
    setConfig(null);
    setParseError(null);
    setImportResult(null);
    setImportSettings(true);
    setImportRoles(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, handleClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div
          className="fixed inset-0 bg-secondary-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="division-config-import-modal-title"
          tabIndex={-1}
          className="relative bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-lg mx-auto z-10 focus:outline-none"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-secondary-200 dark:border-secondary-700">
            <h3 id="division-config-import-modal-title" className="text-lg font-semibold text-secondary-900 dark:text-white">
              Import Division Configuration
            </h3>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
              into <span className="font-medium">{divisionName}</span>
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">
            {/* Step: Upload */}
            {step === 'upload' && (
              <>
                <div
                  className="flex flex-col items-center justify-center border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-10 cursor-pointer hover:border-primary-400 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileJson className="h-10 w-10 text-secondary-400 mb-3" />
                  <p className="text-sm font-medium text-secondary-700 dark:text-secondary-200">
                    Click to select a configuration file
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">.json files only</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,application/json"
                  className="hidden"
                  onChange={handleFileChange}
                />
                {parseError && (
                  <div className="flex items-start gap-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3 text-sm">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{parseError}</span>
                  </div>
                )}
              </>
            )}

            {/* Step: Preview */}
            {step === 'preview' && config && (
              <>
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-md border border-secondary-200 dark:border-secondary-700 p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-secondary-500 dark:text-secondary-400">Source division</span>
                    <span className="font-medium text-secondary-900 dark:text-white">
                      {config.sourceDivisionName}
                      {config.sourceDivisionCode ? ` (${config.sourceDivisionCode})` : ''}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-500 dark:text-secondary-400">Exported at</span>
                    <span className="text-secondary-700 dark:text-secondary-200">
                      {new Date(config.exportedAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-secondary-500 dark:text-secondary-400">Roles</span>
                    <span className="text-secondary-700 dark:text-secondary-200">{config.roles.length}</span>
                  </div>
                </div>

                <fieldset className="space-y-3">
                  <legend className="text-sm font-medium text-secondary-700 dark:text-secondary-200">What to import:</legend>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importSettings}
                      onChange={(e) => setImportSettings(e.target.checked)}
                      disabled={!config.settings}
                      className="h-4 w-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                    <span className={`text-sm ${!config.settings ? 'text-secondary-400' : 'text-secondary-700 dark:text-secondary-200'}`}>
                      Division settings{!config.settings ? ' (none in file)' : ''}
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={importRoles}
                      onChange={(e) => setImportRoles(e.target.checked)}
                      disabled={config.roles.length === 0}
                      className="h-4 w-4 rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                    />
                    <span className={`text-sm ${config.roles.length === 0 ? 'text-secondary-400' : 'text-secondary-700 dark:text-secondary-200'}`}>
                      Roles ({config.roles.length})
                      {config.roles.length > 0 && (
                        <span className="text-secondary-500 dark:text-secondary-400 ml-1">— only adds missing roles</span>
                      )}
                    </span>
                  </label>
                </fieldset>

                {parseError && (
                  <div className="flex items-start gap-2 text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3 text-sm">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <span>{parseError}</span>
                  </div>
                )}
              </>
            )}

            {/* Step: Result */}
            {step === 'result' && importResult && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle className="h-5 w-5" />
                  <span className="font-medium">Import completed successfully</span>
                </div>
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-md border border-secondary-200 dark:border-secondary-700 p-4 space-y-2 text-sm">
                  {importResult.settingsUpdated && (
                    <div className="flex items-center gap-2 text-secondary-700 dark:text-secondary-200">
                      <span className="w-3 h-3 rounded-full bg-primary-500 inline-block" />
                      Division settings updated
                    </div>
                  )}
                  {importResult.rolesCreated > 0 && (
                    <div className="flex items-center gap-2 text-secondary-700 dark:text-secondary-200">
                      <span className="w-3 h-3 rounded-full bg-primary-500 inline-block" />
                      {importResult.rolesCreated} role{importResult.rolesCreated !== 1 ? 's' : ''} created
                    </div>
                  )}
                  {!importResult.settingsUpdated && importResult.rolesCreated === 0 && (
                    <div className="text-secondary-500 dark:text-secondary-400">Nothing was changed — the division is already up to date.</div>
                  )}
                </div>
                {importResult.warnings.length > 0 && (
                  <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-md p-3 space-y-1">
                    {importResult.warnings.map((w, i) => (
                      <div key={i} className="flex items-start gap-2 text-amber-700 dark:text-amber-400 text-sm">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-secondary-200 dark:border-secondary-700 flex justify-end gap-3">
            {step === 'upload' && (
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
            )}
            {step === 'preview' && (
              <>
                <Button variant="secondary" onClick={() => { setStep('upload'); setConfig(null); setParseError(null); }}>
                  Back
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={importMutation.isPending || (!importSettings && !importRoles)}
                >
                  {importMutation.isPending ? 'Importing…' : 'Import'}
                </Button>
              </>
            )}
            {step === 'result' && (
              <Button onClick={handleClose}>Done</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
